import {
  BrowserRouter,
  Navigate,
  useRoutes,
} from 'react-router-dom'
import { useMemo, useEffect, useState } from 'react'
import { useAuthStore } from '@/store/auth'
import { generateRoutesFromUserRoutes } from '@/routes/generateRoutes'
import { HelmetProvider } from 'react-helmet-async'
import { Providers } from '@/components/providers/Providers'
import { SessionProvider } from '@/components/providers/SessionProvider'
import type { RouteObject } from 'react-router-dom'

// Public routes (no authentication required)
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import TwoFactor from '@/pages/TwoFactor'
import TwoFactorSetup from '@/pages/TwoFactor/Setup'


const BASE_PUBLIC_ROUTES: RouteObject[] = [
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <ForgotPassword /> },
  { path: '/reset-password', element: <ResetPassword /> },
  { path: '/two-factor', element: <TwoFactor /> },
  { path: '/two-factor/setup', element: <TwoFactorSetup /> },
  //temp Client Login route without auth until backend ready
]

const UNAUTH_ROUTES: RouteObject[] = [
  ...BASE_PUBLIC_ROUTES,
  { path: '/', element: <Login /> },
  { path: '*', element: <Navigate to="/login" replace /> },
]

function AppRoutes({ routes }: { routes: RouteObject[] }) {
  const element = useRoutes(routes)
  return element
}

function App() {
  const user = useAuthStore((state) => state.user)
  const isLoading = useAuthStore((state) => state.isLoading)
  const token = useAuthStore((state) => state.token)
  const initializeAuth = useAuthStore((state) => state.initializeAuth)
  const [isInitialized, setIsInitialized] = useState(false)

  // Initialize auth on mount and wait for it to complete
  useEffect(() => {
    let isMounted = true
    const init = async () => {
      try {
        await initializeAuth()
        if (isMounted) {
          setIsInitialized(true)
          if (import.meta.env.DEV) {
            console.log('[App] Auth initialization complete')
          }
        }
      } catch (error) {
        console.error('[App] Auth initialization failed:', error)
        if (isMounted) {
          setIsInitialized(true) // Still set to true to prevent infinite loading
        }
      }
    }
    init()
    return () => {
      isMounted = false
    }
  }, [initializeAuth])

  // Memoize routes to prevent unnecessary regenerations
  // Wait for initialization to complete before generating routes
  const routes = useMemo(() => {
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('[App] Generating routes:', {
        isInitialized,
        isLoading,
        hasToken: !!token,
        hasUser: !!user,
        hasUserRoutes: user?.user_routes !== undefined,
        userRoutesLength: user?.user_routes?.length || 0,
      })
    }

    // Show public routes while initializing (but don't show loading screen if we have token)
    if (!isInitialized) {
      if (token) {
        // Generate routes early, but skip catch-all until initialization completes.
        // This prevents redirecting away from the current URL on reload before user_routes load.
        return generateRoutesFromUserRoutes([], BASE_PUBLIC_ROUTES, {
          includeFallback: false,
        })
      }
      if (import.meta.env.DEV) {
        console.log(
          '[App] Using BASE_PUBLIC_ROUTES (not initialized, no token)',
        )
      }
      return BASE_PUBLIC_ROUTES
    }

    // If we have a token or user, try to generate authenticated routes
    if (token || user) {
      // Get routes from user object, with fallback to localStorage if user_routes is undefined
      let routesToUse: any[] = []

      if (user?.user_routes !== undefined) {
        // Use routes from user object (guaranteed to be an array from auth store)
        routesToUse = Array.isArray(user.user_routes) ? user.user_routes : []
      } else if (token && typeof window !== 'undefined') {
        // Fallback: try to get routes from localStorage during initialization
        try {
          const authStorage = localStorage.getItem('auth-storage')
          if (authStorage) {
            const parsed = JSON.parse(authStorage)
            const storedUser = parsed.state?.user
            if (
              storedUser?.user_routes &&
              Array.isArray(storedUser.user_routes)
            ) {
              routesToUse = storedUser.user_routes
              if (import.meta.env.DEV) {
                console.log(
                  '[App] Using routes from localStorage (user_routes not yet loaded):',
                  {
                    routeCount: routesToUse.length,
                  },
                )
              }
            }
          }
        } catch (error) {
          console.error('[App] Error reading routes from localStorage:', error)
        }
      }

      // Generate routes (even if empty array - will still include dashboard)
      const generatedRoutes = generateRoutesFromUserRoutes(
        routesToUse,
        BASE_PUBLIC_ROUTES,
      )

      if (import.meta.env.DEV) {
        console.log('[App] Generated routes:', {
          userRoutesCount: routesToUse.length,
          generatedRoutesCount: generatedRoutes.length,
          routePaths: generatedRoutes.map((r) => r.path),
          hasUser: !!user,
          userRoutesDefined: user?.user_routes !== undefined,
          source: user?.user_routes !== undefined ? 'user' : 'localStorage',
        })
      }

      return generatedRoutes
    }

    // No token or user, show unauthenticated routes
    if (import.meta.env.DEV) {
      console.log('[App] Using UNAUTH_ROUTES (no token or user)')
    }
    return UNAUTH_ROUTES
  }, [user?.user_routes, token, user, isInitialized, isLoading])

  return (
    <HelmetProvider>
      <Providers>
        <BrowserRouter
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <SessionProvider>
            {/* Show loading overlay only during initial auth check, not during login */}
            {!isInitialized && !token ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                }}
              >
                <div>Loading...</div>
              </div>
            ) : (
              <AppRoutes routes={routes} />
            )}
          </SessionProvider>
        </BrowserRouter>
      </Providers>
    </HelmetProvider>
  )
}

export default App
